import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { gruposAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import '../Clientes.css';

const RESUMEN_FORMATO_LABELS = {
  total_sin_detalle: 'Total resumido sin detallar integrantes',
  total_con_nombres_movimientos: 'Total con nombres y movimientos (sin importes)',
  total_detallado_individual: 'Total detallado por integrante y movimientos',
  total_saldos_integrantes: 'Saldo del grupo y saldo por integrante (sin movimientos)',
};

const getSortValueGrupo = (g, colId) => {
  switch (colId) {
    case 'nombre':
      return (g.nombre || '').toString().toLowerCase();
    case 'formato':
      return (RESUMEN_FORMATO_LABELS[g.resumen_formato] || g.resumen_formato || '').toLowerCase();
    case 'integrantes':
      return Number(g.total_integrantes) || 0;
    case 'destinatarios':
      return Number(g.total_destinatarios) || 0;
    case 'estado':
      return g.activo ? 1 : 0;
    default:
      return '';
  }
};

const Grupos = () => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const { puedeGestionarGrupos } = useAuth();
  const puedeEditar = puedeGestionarGrupos();

  useEffect(() => {
    loadGrupos();
  }, []);

  const loadGrupos = async () => {
    try {
      setLoading(true);
      const response = puedeEditar
        ? await gruposAPI.getAllIncludingInactive()
        : await gruposAPI.getAll();
      if (response.success) {
        setGrupos(response.data);
      }
    } catch (error) {
      toast.error(error.message || 'Error al cargar grupos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este grupo?')) return;
    try {
      const response = await gruposAPI.delete(id);
      if (response.success) {
        toast.success('Grupo eliminado exitosamente');
        loadGrupos();
      }
    } catch (error) {
      toast.error(error.message || 'Error al eliminar grupo');
    }
  };

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) {
      setSortAsc((prev) => !prev);
    } else {
      setSortByColumn(columnId);
      setSortAsc(true);
    }
  };

  const gruposVisibles = useMemo(() => {
    let list = [...grupos];
    const q = busqueda.trim().toLowerCase();
    if (q) {
      list = list.filter((g) => (g.nombre || '').toLowerCase().includes(q));
    }
    if (filtroEstado === 'activo') list = list.filter((g) => g.activo);
    if (filtroEstado === 'inactivo') list = list.filter((g) => !g.activo);
    if (sortByColumn) {
      list.sort((a, b) => {
        const v1 = getSortValueGrupo(a, sortByColumn);
        const v2 = getSortValueGrupo(b, sortByColumn);
        const num = typeof v1 === 'number' && typeof v2 === 'number';
        let cmp = 0;
        if (num) cmp = v1 - v2;
        else cmp = String(v1).localeCompare(String(v2), 'es');
        return sortAsc ? cmp : -cmp;
      });
    }
    return list;
  }, [grupos, busqueda, filtroEstado, sortByColumn, sortAsc]);

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Grupos de clientes</h1>
        {puedeEditar && (
          <Link to="/grupos/nuevo" className="btn-primary">+ Nuevo Grupo</Link>
        )}
      </div>

      <div className="pagina-listado-fijo">
        <p style={{ color: '#666', marginBottom: 20, lineHeight: 1.5 }}>
          Los grupos agrupan clientes para enviar resúmenes consolidados. Asigne integrantes al grupo,
          marque quién es <strong>destinatario</strong> de las comunicaciones y envíe desde la lista de
          Clientes con la opción <em>Por grupo</em>.
        </p>
      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters-toolbar" style={{ marginBottom: 0, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Buscar por nombre..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ minWidth: 220, padding: '8px 10px' }}
        />
        {puedeEditar && (
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={{ padding: '8px 10px' }}>
            <option value="todos">Todos los estados</option>
            <option value="activo">Solo activos</option>
            <option value="inactivo">Solo inactivos</option>
          </select>
        )}
        </div>

        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className="listado-tabla-area clientes-listado-tabla-area">
            <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {[
                { id: 'nombre', label: 'Nombre' },
                { id: 'formato', label: 'Formato de resumen' },
                { id: 'integrantes', label: 'Integrantes' },
                { id: 'destinatarios', label: 'Destinatarios' },
                { id: 'estado', label: 'Estado' },
              ].map((col) => (
                <th
                  key={col.id}
                  className="th-sortable"
                  onClick={() => handleSortColumn(col.id)}
                  title={`Ordenar por ${col.label}`}
                >
                  <span>{col.label}</span>
                  {sortByColumn === col.id && (
                    <span className="sort-indicator" aria-hidden="true">{sortAsc ? ' ▲' : ' ▼'}</span>
                  )}
                </th>
              ))}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {gruposVisibles.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">No hay grupos que coincidan</td>
              </tr>
            ) : (
              gruposVisibles.map((grupo) => (
                <tr key={grupo.id}>
                  <td><strong>{grupo.nombre}</strong></td>
                  <td>{RESUMEN_FORMATO_LABELS[grupo.resumen_formato] || grupo.resumen_formato}</td>
                  <td style={{ fontSize: 13 }}>
                    {grupo.total_integrantes ?? 0} integrante(s)
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {grupo.total_destinatarios ?? 0} destinatario(s)
                  </td>
                  <td>
                    <span className={`badge ${grupo.activo ? 'activo' : 'inactivo'}`}>
                      {grupo.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {puedeEditar && (
                        <>
                          <Link
                            to={`/grupos/editar/${grupo.id}#destinatarios`}
                            className="btn-secondary"
                            style={{ marginRight: 6 }}
                          >
                            Configurar envío
                          </Link>
                          <Link to={`/grupos/editar/${grupo.id}`} className="btn-edit">Editar</Link>
                          <button type="button" onClick={() => handleDelete(grupo.id)} className="btn-delete">Eliminar</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Grupos;
